
import { getSafeClient } from "@/integrations/supabase/safeClient";
import type { EnhancedOffer } from "./types";
import { transformSupabaseOffer } from "./transform";

const OFFER_SELECT_QUERY = `
  id,
  title,
  price_cents,
  dr,
  da,
  traffic_estimate,
  link_type,
  turnaround_days,
  tags,
  seller_profile_id,
  description,
  slug,
  seller_profiles (
    name,
    verified,
    slug
  )
`;

export async function fetchOffers(): Promise<EnhancedOffer[]> {
  try {
    console.log("fetchOffers: Starting fetch...");
    
    const supabaseClient = await getSafeClient();
    console.log("Using Supabase client:", supabaseClient);
    
    const { data, error } = await supabaseClient
      .from("offers")
      .select(OFFER_SELECT_QUERY)
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching offers:", error);
      console.error("Error details:", JSON.stringify(error));
      throw new Error(error.message);
    }

    console.log("fetchOffers: Data received:", data?.length || 0, "items");
    console.log("Sample offer data:", data?.[0]);
    
    return data.map(transformSupabaseOffer);
  } catch (error) {
    console.error("Error in fetchOffers:", error);
    console.error("Error stack:", error instanceof Error ? error.stack : "No stack available");
    throw error;
  }
}

export async function fetchFeaturedOffers(): Promise<EnhancedOffer[]> {
  try {
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("offers")
      .select(OFFER_SELECT_QUERY)
      .eq("is_active", true)
      .eq("is_featured", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching featured offers:", error);
      throw new Error(error.message);
    }

    return data.map(transformSupabaseOffer);
  } catch (error) {
    console.error("Error in fetchFeaturedOffers:", error);
    throw error;
  }
}

export async function fetchOfferById(offerId: string): Promise<EnhancedOffer | null> {
  try {
    if (!offerId) {
      throw new Error("Offer ID is required");
    }
    
    console.log(`Fetching offer details for ID: ${offerId}`);
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("offers")
      .select(OFFER_SELECT_QUERY)
      .eq("id", offerId)
      .eq("is_active", true)
      .single();

    if (error) {
      console.error(`Error fetching offer with ID ${offerId}:`, error);
      return null;
    }

    if (!data) {
      console.log(`No offer found with ID ${offerId}`);
      return null;
    }

    console.log(`Raw data for offer ID ${offerId}:`, data);
    const transformedData = transformSupabaseOffer(data);
    console.log(`Transformed data for offer ID ${offerId}:`, transformedData);
    return transformedData;
  } catch (error) {
    console.error(`Error in fetchOfferById for ID ${offerId}:`, error);
    throw error;
  }
}

export async function fetchOfferBySlug(slug: string): Promise<EnhancedOffer | null> {
  try {
    if (!slug) {
      throw new Error("Offer slug is required");
    }
    
    console.log(`Fetching offer details for slug: ${slug}`);
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("offers")
      .select(OFFER_SELECT_QUERY)
      .eq("slug", slug)
      .eq("is_active", true)
      .single();

    if (error) {
      console.error(`Error fetching offer with slug ${slug}:`, error);
      return null;
    }

    if (!data) {
      console.log(`No offer found with slug ${slug}`);
      return null;
    }

    console.log(`Raw data for offer slug ${slug}:`, data);
    const transformedData = transformSupabaseOffer(data);
    console.log(`Transformed data for offer slug ${slug}:`, transformedData);
    return transformedData;
  } catch (error) {
    console.error(`Error in fetchOfferBySlug for slug ${slug}:`, error);
    throw error;
  }
}

export async function fetchOffersBySellerId(sellerId: string): Promise<EnhancedOffer[]> {
  try {
    if (!sellerId) {
      throw new Error("Seller ID is required");
    }
    
    const supabaseClient = await getSafeClient();
    
    const { data, error } = await supabaseClient
      .from("offers")
      .select(OFFER_SELECT_QUERY)
      .eq("seller_profile_id", sellerId)
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching offers by seller ID:", error);
      throw new Error(error.message);
    }

    return data.map(transformSupabaseOffer);
  } catch (error) {
    console.error("Error in fetchOffersBySellerId:", error);
    throw error;
  }
}

export async function fetchOffersBySellerSlug(sellerSlug: string): Promise<EnhancedOffer[]> {
  try {
    if (!sellerSlug) {
      throw new Error("Seller slug is required");
    }
    
    console.log(`Fetching offers for seller slug: ${sellerSlug}`);
    const supabaseClient = await getSafeClient();
    
    // First get the seller profile id from slug
    const { data: sellerData, error: sellerError } = await supabaseClient
      .from("seller_profiles")
      .select("id")
      .eq("slug", sellerSlug)
      .single();
      
    if (sellerError || !sellerData) {
      console.error(`Error or no seller found with slug ${sellerSlug}:`, sellerError);
      return [];
    }
    
    const sellerId = sellerData.id;
    
    const { data, error } = await supabaseClient
      .from("offers")
      .select(OFFER_SELECT_QUERY)
      .eq("seller_profile_id", sellerId)
      .eq("is_active", true)
      .order("created_at", { ascending: false });

    if (error) {
      console.error(`Error fetching offers for seller slug ${sellerSlug}:`, error);
      return [];
    }

    return data.map(transformSupabaseOffer);
  } catch (error) {
    console.error(`Error in fetchOffersBySellerSlug for slug ${sellerSlug}:`, error);
    return [];
  }
}
